import * as React from "react";
import { DragDropManager } from "@dnd-kit/dom";
import { isSortable, Sortable } from "@dnd-kit/dom/sortable";
import { DeleteOutlined, ExpandOutlined, InfoCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Popover, theme, Typography } from "antd";
import { SlideDetailsPopoverChip } from "@/components/SlideDetailsPopoverChip";
import { SlidePreviewEnlargeModal } from "@/components/SlidePreviewEnlargeModal";
import { usePresentationStore } from "@/state/presentationStore";
import Ring from "@/components/ui/Ring";
import DropdownMenuField from "@/components/ui/DropdownMenuField";

const { Title } = Typography;

const SLIDE_CATEGORY_HELP = (
  <p className="m-0 text-sm text-gray-800 leading-relaxed">
    <strong>Choose a Category: </strong> Choose the category of Slide Precedent you want. The available slide precedents below will update based on your
    selection.
    <br />
    <strong>Front Cover: </strong> Appropriate text will be added to the slide to frame the presentation you have created. 
    <br />
    <strong>Section Divider: </strong> Appropriate text will be added to the slide to frame the section you have created. 
    <br />
    <strong>Disclaimer: </strong> Text will be added as per the Format Template you choose. 
  </p>
);
const AVAILABLE_SLIDES_HELP = (
  <p className="m-0 text-sm text-gray-800 leading-relaxed">
   <strong>Review Available Slides: </strong> This panel shows slide precedents available for the selected category. Select “Add” button to include any slide into the presentation outline </p>
);

const PRESENTATION_OUTLINE_HELP = (
  <p className="m-0 text-sm text-gray-800 leading-relaxed">
    <strong>Adjust Your Outline:</strong>  Arrange your selected slides here. You can reorder slides, add more from the left panel, or remove slides before proceeding.</p>
);
const slideCategoryItems: { label: string; key: string }[] = [
  { label: "Share Price Performance", key: "sharePricePerformance" },
  { label: "Structuring Slides", key: "structuringSlides" },
];

const SHARE_PRICE_SLIDE_SRC = [1, 2, 3, 4, 5, 6, 7, 8, 9].map(
  (n) => `assets/slides/${encodeURIComponent(`Slide${n}.PNG`)}`
);

const STRUCTURING_SLIDE_SRC = [
  `assets/slides/${encodeURIComponent("Front Cover.png")}`,
  `assets/slides/${encodeURIComponent("Section Divider.png")}`,
  `assets/slides/${encodeURIComponent("disclaimer.png")}`,
];

const ALL_AVAILABLE_SLIDE_SRC = [...SHARE_PRICE_SLIDE_SRC, ...STRUCTURING_SLIDE_SRC];

const PRESENTATION_SLIDE_DETAILS_BODY =
  "This precedent slide guides layout and structure. We will update it for the selected primary company, time period, relative benchmarks, and other contextual inputs.";

const OUTLINE_SORTABLE_TRANSITION = { duration: 300, easing: "ease" } as const;

const OUTLINE_WIDE_MEDIA = "(min-width: 1400px)";
const OUTLINE_SCROLL_AFTER_COUNT_WIDE = 6;
const OUTLINE_SCROLL_AFTER_COUNT_NARROW = 4;

const OUTLINE_SLIDE_ACTION_CHIP =
  "inline-flex h-8 shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-[#d9dde3] bg-white px-3 text-[13px] font-medium leading-none text-[#1f2328] shadow-[0_1px_3px_rgba(0,0,0,0.08)] transition-[background-color,border-color,box-shadow,transform] duration-[120ms] ease-out hover:border-[#cdd3db] hover:bg-[#f8f9fb] hover:shadow-[0_2px_6px_rgba(0,0,0,0.10)] active:translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-1";

const OUTLINE_SLIDE_DELETE_CHIP =
  "inline-flex h-8 shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-red-600 bg-white px-3 text-[13px] font-medium leading-none text-red-600 shadow-[0_1px_3px_rgba(0,0,0,0.08)] transition-[background-color,border-color,box-shadow,transform] duration-[120ms] ease-out hover:border-red-700 hover:bg-red-50 hover:shadow-[0_2px_6px_rgba(0,0,0,0.10)] active:translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1";

const OUTLINE_SLIDE_CHIP_ICON = "inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center text-current";

/** Compact chips for narrow “Precedent Slides” column (~25% larger than prior compact). */
const AVAILABLE_SLIDE_ACTION_CHIP =
  "inline-flex h-5 shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-[#d9dde3] bg-white px-2.5 text-[12.5px] font-medium leading-none text-[#1f2328] shadow-[0_1px_3px_rgba(0,0,0,0.07)] transition-[background-color,border-color,box-shadow,transform] duration-[120ms] ease-out hover:border-[#cdd3db] hover:bg-[#f8f9fb] hover:shadow-[0_2px_5px_rgba(0,0,0,0.09)] active:translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-0";

const AVAILABLE_SLIDE_ADD_CHIP =
  "inline-flex h-5 shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 text-[12.5px] font-medium leading-none text-white shadow-[0_1px_3px_rgba(0,0,0,0.07)] transition-[background-color,border-color,box-shadow,transform] duration-[120ms] ease-out hover:brightness-95 active:translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-0";

const AVAILABLE_SLIDE_CHIP_ICON = "inline-flex h-3 w-3 shrink-0 items-center justify-center text-current";

function useOutlineWideLayout() {
  const getSnapshot = React.useCallback(
    () => (typeof window !== "undefined" ? window.matchMedia(OUTLINE_WIDE_MEDIA).matches : false),
    []
  );

  return React.useSyncExternalStore(
    (onStoreChange) => {
      if (typeof window === "undefined") return () => {};
      const mq = window.matchMedia(OUTLINE_WIDE_MEDIA);
      mq.addEventListener("change", onStoreChange);
      return () => mq.removeEventListener("change", onStoreChange);
    },
    getSnapshot,
    () => false
  );
}

type OutlineSortableSlideProps = {
  manager: DragDropManager;
  frameId: number;
  src: string;
  index: number;
  getSlideNumberFromSrc: (src: string) => number;
  onRemove: (id: number) => void;
  detailsPopoverContent: React.ReactNode;
  onOpenEnlarge: (frameId: number) => void;
  columnClassName: string;
  emphasizeDrag?: boolean;
  dragAccentStyle?: React.CSSProperties;
};

function OutlineSortableSlide({
  manager,
  frameId,
  src,
  index,
  getSlideNumberFromSrc,
  onRemove,
  detailsPopoverContent,
  onOpenEnlarge,
  columnClassName,
  emphasizeDrag = false,
  dragAccentStyle,
}: OutlineSortableSlideProps) {
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const sortableRef = React.useRef<Sortable | null>(null);

  React.useLayoutEffect(() => {
    const element = rootRef.current;
    if (!element) return undefined;

    const sortable = new Sortable(
      {
        id: frameId,
        index,
        element,
        transition: OUTLINE_SORTABLE_TRANSITION,
      },
      manager
    );

    sortableRef.current = sortable;

    return () => {
      sortable.destroy();
      sortableRef.current = null;
    };
  }, [frameId, manager]);

  React.useLayoutEffect(() => {
    const sortable = sortableRef.current;
    if (sortable) sortable.index = index;
  }, [index]);

  return (
    <div ref={rootRef} className={columnClassName}>
      <div
        className={
          "group relative box-border w-full cursor-grab overflow-hidden rounded-md border-2 bg-white text-gray-600 shadow-sm outline-none transition-[transform,box-shadow,border-color] duration-300 ease-in-out active:cursor-grabbing " +
          (emphasizeDrag
            ? "border-[color:var(--precedent-primary)] shadow-md hover:border-[color:var(--precedent-primary-hover)] hover:shadow-[0_10px_24px_rgba(0,0,0,0.12),0_2px_6px_rgba(0,0,0,0.06)] z-10 "
            : "border-gray-200 hover:border-[#ff6a00] hover:shadow-[0_10px_24px_rgba(0,0,0,0.12),0_2px_6px_rgba(0,0,0,0.06)] ") +
          "aria-[grabbed=true]:scale-[1.025] aria-[grabbed=true]:shadow-[inset_0_0_1px_rgba(0,0,0,0.5),-1px_0_15px_0_rgba(34,33,81,0.01),0_15px_15px_0_rgba(34,33,81,0.25)]"
        }
        style={emphasizeDrag && dragAccentStyle ? dragAccentStyle : undefined}
      >
        {emphasizeDrag ? (
          <>
            <div
              className="pointer-events-none absolute inset-0 z-[1] rounded-md"
              style={{
                backgroundColor: "color-mix(in srgb, var(--precedent-primary) 8%, transparent)",
              }}
              aria-hidden
            />
            <div
              className="pointer-events-none absolute inset-0 z-[1] rounded-md bg-black/10 opacity-0 transition-opacity duration-[160ms] ease-out group-hover:opacity-100"
              aria-hidden
            />
          </>
        ) : (
          <div
            className="pointer-events-none absolute inset-0 z-[1] rounded-md bg-[rgba(0,0,0,0.09)] opacity-0 transition-opacity duration-[160ms] ease-out group-hover:opacity-100 group-focus-within:opacity-100"
            aria-hidden
          />
        )}
        <div className="relative z-0 aspect-video w-full">
          <img src={src} alt="" className="h-full w-full object-contain" loading="lazy" draggable={false} />
        </div>
        <div
          className="pointer-events-none absolute bottom-3 px-3 z-[2] flex w-full min-w-0 items-center justify-between gap-2 opacity-0 transition-[opacity,transform] duration-[160ms] ease-out [transform:translateY(4px)] group-hover:pointer-events-auto group-hover:opacity-100 group-hover:[transform:translateY(0)] group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-focus-within:[transform:translateY(0)]"
          role="presentation"
        >
          <div className="flex min-w-0 shrink items-center gap-2">
            <button
              type="button"
              className={OUTLINE_SLIDE_ACTION_CHIP}
              aria-label={`Enlarge preview of outline slide ${index + 1}`}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => onOpenEnlarge(frameId)}
            >
              <span className={OUTLINE_SLIDE_CHIP_ICON}>
                <ExpandOutlined className="!block !h-3.5 !w-3.5" />
              </span>
              Enlarge
            </button>
            <SlideDetailsPopoverChip
              content={detailsPopoverContent}
              triggerClassName={OUTLINE_SLIDE_ACTION_CHIP}
              ariaLabel={`Details for outline slide ${index + 1}`}
            />
          </div>
          <button
            type="button"
            className={`${OUTLINE_SLIDE_DELETE_CHIP} shrink-0`}
            aria-label={`Delete slide ${getSlideNumberFromSrc(src) || index + 1} from outline`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => onRemove(frameId)}
          >
            Delete
            <span className={OUTLINE_SLIDE_CHIP_ICON}>
              <DeleteOutlined className="!block !h-3.5 !w-3.5" />
            </span>
          </button>
        </div>
      </div>
      <div className=" px-1.5 py-1 text-right font-bold text-[12px] text-black">Slide {index + 1}</div>
    </div>
  );
}

/** Placeholder step before Initial Input; replace with outline editor / import UI later. */
export default function PresentationOutlineStep() {
  const currentStep = usePresentationStore((s) => s.currentStep);
  const setAnswersByStep = usePresentationStore((s) => s.setAnswersByStep);
  const questionsPerStep = usePresentationStore((s) => s.questionsPerStep);
  const sharePriceType = usePresentationStore((s) => s.sharePriceType);
  const setSharePriceType = usePresentationStore((s) => s.setSharePriceType);

  const selectedFrames = usePresentationStore((s) => s.selectedOutlineFrames);
  const addFrame = usePresentationStore((s) => s.addOutlineFrame);
  const removeFrame = usePresentationStore((s) => s.removeOutlineFrame);

  const [draggingOutlineFrameId, setDraggingOutlineFrameId] = React.useState<number | null>(null);
  const [outlineEnlargeFrameId, setOutlineEnlargeFrameId] = React.useState<number | null>(null);
  /** `src` of the slide shown in the enlarge modal from the left “Precedent Slides” list. */
  const [availableEnlargeSrc, setAvailableEnlargeSrc] = React.useState<string | null>(null);

  const dragDropManagerRef = React.useRef<DragDropManager | null>(null);
  if (!dragDropManagerRef.current) {
    dragDropManagerRef.current = new DragDropManager();
  }
  const outlineDragDropManager = dragDropManagerRef.current;

  React.useEffect(() => {
    return () => {
      outlineDragDropManager.destroy();
      dragDropManagerRef.current = null;
    };
  }, [outlineDragDropManager]);

  React.useEffect(() => {
    const manager = dragDropManagerRef.current;
    if (!manager) return undefined;

    const unsubDragStart = manager.monitor.addEventListener("dragstart", (event) => {
      const src = event.operation.source;
      if (!src || !isSortable(src)) return;
      if (typeof src.id === "number") {
        setDraggingOutlineFrameId(src.id);
      }
    });
    const unsubDragEndVisual = manager.monitor.addEventListener("dragend", () => {
      setDraggingOutlineFrameId(null);
    });

    return () => {
      unsubDragStart();
      unsubDragEndVisual();
    };
  }, []);

  React.useEffect(() => {
    const manager = dragDropManagerRef.current;
    if (!manager) return undefined;

    return manager.monitor.addEventListener("dragend", (event) => {
      if (event.canceled) return;
      const source = event.operation.source;
      if (!isSortable(source)) return;
      const { initialIndex, index } = source.sortable;
      if (initialIndex === index) return;

      usePresentationStore.setState((state) => {
        const frames = state.selectedOutlineFrames;
        if (initialIndex < 0 || initialIndex >= frames.length || index < 0 || index >= frames.length) {
          return state;
        }
        const next = [...frames];
        const [moved] = next.splice(initialIndex, 1);
        next.splice(index, 0, moved);
        return { selectedOutlineFrames: next };
      });
    });
  }, []);

  React.useEffect(() => {
    setAnswersByStep(currentStep, questionsPerStep[currentStep] ?? 1);
  }, [currentStep, questionsPerStep, setAnswersByStep]);

  const { token } = theme.useToken();
  const buttonToken = (token as { Button?: { colorPrimary?: string; colorPrimaryHover?: string } }).Button;
  const primaryColor = buttonToken?.colorPrimary ?? token.colorPrimary;
  const primaryHover = buttonToken?.colorPrimaryHover ?? token.colorPrimaryHover ?? primaryColor;
  const primaryFill = primaryColor;

  const outlineDragAccentStyle = React.useMemo(
    () =>
      ({
        ["--precedent-primary"]: primaryColor,
        ["--precedent-primary-hover"]: primaryHover,
      }) as React.CSSProperties,
    [primaryColor, primaryHover]
  );

  const getSlideNumberFromSrc = React.useCallback((src: string) => {
    const idx = ALL_AVAILABLE_SLIDE_SRC.indexOf(src);
    return idx >= 0 ? idx + 1 : 0;
  }, []);

  const availableSlides = React.useMemo(() => {
    return sharePriceType === "structuringSlides" ? STRUCTURING_SLIDE_SRC : SHARE_PRICE_SLIDE_SRC;
  }, [sharePriceType]);

  const availableEnlargeSlides = React.useMemo(
    () => availableSlides.map((src, i) => ({ id: src, title: `Slide ${i + 1}`, src })),
    [availableSlides]
  );

  React.useEffect(() => {
    if (availableEnlargeSrc != null && !availableSlides.includes(availableEnlargeSrc)) {
      setAvailableEnlargeSrc(null);
    }
  }, [availableSlides, availableEnlargeSrc]);

  const presentationDetailsPopoverContent = React.useMemo(
    () => (
      <div className="max-w-xs text-sm text-gray-700">
        <p className="m-0 font-medium text-gray-900">Slide Details</p>
        <p className="mb-0 mt-1">{PRESENTATION_SLIDE_DETAILS_BODY}</p>
        <div className="mt-2">
          <span className="inline-flex items-center rounded-md bg-blue-500 px-3 py-2 text-[12px] font-medium leading-none text-white">
            {sharePriceType === "structuringSlides" ? "Structuring Slides" : "Share Price Performance"}
          </span>
        </div>
      </div>
    ),
    [sharePriceType]
  );

  const outlineWideLayout = useOutlineWideLayout();
  const outlineScrollClassName = React.useMemo(() => {
    const n = selectedFrames.length;
    const threshold = outlineWideLayout ? OUTLINE_SCROLL_AFTER_COUNT_WIDE : OUTLINE_SCROLL_AFTER_COUNT_NARROW;
    const needsScroll = n > threshold;
    const base = "max-h-[67vh] w-full min-w-0 overscroll-contain";
    return needsScroll ? `${base} overflow-x-hidden overflow-y-auto` : `${base} overflow-hidden`;
  }, [outlineWideLayout, selectedFrames.length]);

  const outlineEnlargeSlides = React.useMemo(
    () => selectedFrames.map((f, i) => ({ id: f.id, title: `Slide ${i + 1}`, src: f.src })),
    [selectedFrames]
  );
  const outlineEnlargeActive =
    outlineEnlargeFrameId == null ? null : outlineEnlargeSlides.find((s) => s.id === outlineEnlargeFrameId) ?? null;

  React.useEffect(() => {
    if (outlineEnlargeFrameId != null && !selectedFrames.some((f) => f.id === outlineEnlargeFrameId)) {
      setOutlineEnlargeFrameId(null);
    }
  }, [selectedFrames, outlineEnlargeFrameId]);

  const enlargeMode = outlineEnlargeFrameId != null ? "outline" : availableEnlargeSrc != null ? "available" : null;
  const enlargeSlides = enlargeMode === "outline" ? outlineEnlargeSlides : availableEnlargeSlides;
  const enlargeActive =
    enlargeMode === "outline"
      ? outlineEnlargeActive
      : availableEnlargeSrc != null
        ? (availableEnlargeSlides.find((s) => s.id === availableEnlargeSrc) ?? null)
        : null;
  const enlargeIsAvailablePanel = enlargeMode === "available";

  const openAvailableEnlarge = React.useCallback((src: string) => {
    setOutlineEnlargeFrameId(null);
    setAvailableEnlargeSrc(src);
  }, []);

  const closeEnlargeModal = React.useCallback(() => {
    setOutlineEnlargeFrameId(null);
    setAvailableEnlargeSrc(null);
  }, []);

  return (
    <div className="flex w-full gap-4">
      <Ring className="flex flex-col w-1/5 p-4 gap-6 !bg-white !border !border-gray-200">
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Title level={4} className="!mb-0">
              Slide Category
            </Title>
            <Popover content={SLIDE_CATEGORY_HELP} trigger="click" overlayStyle={{ maxWidth: 380 }}>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded p-0.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
                aria-label="More information about slide category"
              >
                <InfoCircleOutlined className="text-lg" />
              </button>
            </Popover>
          </div>
          <DropdownMenuField
            title=""
            itemslist={slideCategoryItems}
            initialValue={sharePriceType}
            onSelect={(value: string) => setSharePriceType(value)}
          />
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Title level={4} className="!mb-0">
            Precedent Slides
            </Title>
            <Popover content={AVAILABLE_SLIDES_HELP} trigger="click" overlayStyle={{ maxWidth: 380 }}>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded p-0.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
                aria-label="More information about available slides"
              >
                <InfoCircleOutlined className="text-lg" />
              </button>
            </Popover>
          </div>
          <div
            className="max-h-[62vh] w-full overflow-y-auto overscroll-contain"
            aria-label="Available slide precedents"
          >
            <div className="flex w-full flex-col gap-2">
              {availableSlides.map((src, idx) => (
                <div
                  key={src}
                  className="group relative w-full overflow-hidden rounded-md border-2 border-gray-200 bg-white text-gray-600 shadow-sm outline-none transition-[box-shadow,border-color] duration-[160ms] ease-out hover:border-[#ff6a00] hover:shadow-[0_10px_24px_rgba(0,0,0,0.12),0_2px_6px_rgba(0,0,0,0.06)] focus-within:border-[#ff6a00] focus-within:shadow-[0_10px_24px_rgba(0,0,0,0.12),0_2px_6px_rgba(0,0,0,0.06)] focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
                >
                  <div
                    className="pointer-events-none absolute inset-0 z-[1] rounded-md bg-[rgba(0,0,0,0.09)] opacity-0 transition-opacity duration-[160ms] ease-out group-hover:opacity-100 group-focus-within:opacity-100"
                    aria-hidden
                  />
                  <div className="relative z-0 aspect-video w-full">
                    <img src={src} alt="" className="h-full w-full object-contain" loading="lazy" draggable={false} />
                  </div>
                  <div
                    className="pointer-events-none absolute bottom-2.5 px-2.5 z-[2] flex w-full flex-wrap items-center gap-1.5 opacity-0 transition-[opacity,transform] duration-[160ms] ease-out [transform:translateY(4px)] group-hover:pointer-events-auto group-hover:opacity-100 group-hover:[transform:translateY(0)] group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-focus-within:[transform:translateY(0)]"
                    role="presentation"
                  >
                    <button
                      type="button"
                      className={AVAILABLE_SLIDE_ACTION_CHIP}
                      aria-label={`Enlarge preview of available slide ${idx + 1}`}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={() => openAvailableEnlarge(src)}
                    >
                      <span className={AVAILABLE_SLIDE_CHIP_ICON}>
                        <ExpandOutlined className="!block !h-3 !w-3" />
                      </span>
                      Enlarge
                    </button>
                    <SlideDetailsPopoverChip
                      content={presentationDetailsPopoverContent}
                      triggerClassName={AVAILABLE_SLIDE_ACTION_CHIP}
                      ariaLabel={`Details for available slide ${idx + 1}`}
                      iconWrapClassName={AVAILABLE_SLIDE_CHIP_ICON}
                      iconClassName="!h-3 !w-3"
                      overlayMaxWidth={360}
                    />
                    <span className="min-w-1 flex-1" aria-hidden />
                    <button
                      type="button"
                      className={AVAILABLE_SLIDE_ADD_CHIP}
                      style={{ backgroundColor: primaryFill, borderColor: primaryFill }}
                      aria-label={`Add slide ${idx + 1} to presentation outline`}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={() => addFrame(src)}
                    >
                      <span className={AVAILABLE_SLIDE_CHIP_ICON}>
                        <PlusOutlined className="!block !h-3 !w-3" />
                      </span>
                      Add
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>       
        </div>
      </Ring>

      <Ring className="flex w-4/5 p-4 !border !border-gray-200">
        <div className="flex flex-col gap-2 w-full">
          <div className="flex flex-wrap items-center gap-2 w-full">
            <Title level={4} className="!mb-0">
              Presentation Outline
            </Title>
            <Popover content={PRESENTATION_OUTLINE_HELP} trigger="click" overlayStyle={{ maxWidth: 380 }}>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded p-0.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
                aria-label="More information about slide category"
              >
                <InfoCircleOutlined className="text-lg" />
              </button>
            </Popover>
          </div>
          <div className=" w-full">
            <Title level={5} className="!mb-0 !text-black !font-normal">
            Add Precedent Slides – Drag Slides To Reorder Presentation
            </Title>
          </div>
          <div className="w-full">
            {selectedFrames.length === 0 ? (
              <div className="rounded border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">
                No slides selected yet. Click <span className="font-semibold">Add</span> on the left to include slides here.
              </div>
            ) : (
              <div className={outlineScrollClassName}>
                <div className="flex flex-wrap gap-2.5">
                  {selectedFrames.map((item, index) => (
                    <OutlineSortableSlide
                      key={item.id}
                      manager={outlineDragDropManager}
                      frameId={item.id}
                      src={item.src}
                      index={index}
                      getSlideNumberFromSrc={getSlideNumberFromSrc}
                      onRemove={removeFrame}
                      detailsPopoverContent={presentationDetailsPopoverContent}
                      onOpenEnlarge={(frameId) => {
                        setAvailableEnlargeSrc(null);
                        setOutlineEnlargeFrameId(frameId);
                      }}
                      columnClassName="flex w-[calc((100%-1.25rem)/3)] min-w-0 flex-col"
                      emphasizeDrag={draggingOutlineFrameId === item.id}
                      dragAccentStyle={outlineDragAccentStyle}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </Ring>

      <SlidePreviewEnlargeModal
        open={enlargeActive != null}
        onClose={closeEnlargeModal}
        slides={enlargeSlides}
        activeSlide={enlargeActive}
        onActiveSlideChange={(s) => {
          if (enlargeMode === "outline") {
            setOutlineEnlargeFrameId(s.id as number);
          } else if (enlargeMode === "available") {
            setAvailableEnlargeSrc(String(s.id));
          }
        }}
        headerTitle={enlargeIsAvailablePanel ? "Precedent Slides" : "Presentation Outline"}
        primaryActionLabel={enlargeIsAvailablePanel ? "Add this Slide" : "Back to Presentation Outline"}
        onPrimaryAction={() => {
          if (enlargeIsAvailablePanel && enlargeActive) {
            addFrame(enlargeActive.src);
            closeEnlargeModal();
            return;
          }
          closeEnlargeModal();
        }}
      />
    </div>
  );
}
